import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Bug, Check, Copy, Trash2, X } from 'lucide-react'
import { Card, CardContent } from './ui/card'
import { Button } from './ui/button'
import { clearEntries, formatForClipboard, getEntries, isEnabled, subscribe } from '../lib/debugLog'
import { cn } from '../lib/utils'

// Floating bug button + bottom sheet. Mounted globally from App.jsx,
// renders nothing unless `phoenix.debug.enabled` is true in
// localStorage (toggled from Settings → Developer). The badge on the
// button shows how many errors are in the current ring buffer so a
// fresh issue is visible without opening the sheet.
//
// Touch targets, z-index, and safe-area inset all sized to coexist
// with the bottom tab bar (we sit above it like the news selection
// pill from PR #328).
const LEVEL_TONES = {
  log:   'text-foreground',
  info:  'text-app-primary-500',
  warn:  'text-amber-600 dark:text-amber-400',
  error: 'text-red-500',
  debug: 'text-muted-foreground',
}

export default function DebugOverlay() {
  const { t } = useTranslation()
  // Track enabled state via a `storage` listener so flipping the
  // setting toggle propagates without a reload. We also re-poll on
  // visibilitychange because some iOS PWAs miss storage events on
  // cold start.
  const [enabled, setEnabled] = useState(isEnabled)
  useEffect(() => {
    const sync = () => setEnabled(isEnabled())
    window.addEventListener('storage', sync)
    document.addEventListener('visibilitychange', sync)
    return () => {
      window.removeEventListener('storage', sync)
      document.removeEventListener('visibilitychange', sync)
    }
  }, [])

  const [entries, setEntries] = useState(() => getEntries())
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [filter, setFilter] = useState('all') // 'all' | 'error' | 'warn'

  // Subscribe to log updates so the badge + sheet stay live without
  // having to manually refresh. Unsubscribe is automatic on unmount.
  useEffect(() => {
    return subscribe(setEntries)
  }, [])

  // Reset the "copied" indicator a moment after the user taps Copy.
  useEffect(() => {
    if (!copied) return undefined
    const t = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(t)
  }, [copied])

  const filtered = useMemo(() => {
    if (filter === 'error') return entries.filter((e) => e.level === 'error')
    if (filter === 'warn') return entries.filter((e) => e.level === 'warn' || e.level === 'error')
    return entries
  }, [entries, filter])

  const errorCount = useMemo(() => entries.filter((e) => e.level === 'error').length, [entries])

  const handleCopy = async () => {
    const text = formatForClipboard(entries)
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        // Fallback for older iOS — temp textarea + execCommand.
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
    } catch { /* clipboard unavailable in some PWA contexts */ }
  }

  if (!enabled || typeof document === 'undefined') return null

  return createPortal(
    <>
      {/* Floating launcher button — bottom-end, above the tab bar
          and the news selection pill (z-index 60). Badge shows
          unread error count. */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('debug.open')}
          style={{
            position: 'fixed',
            right: 12,
            bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
            zIndex: 65,
          }}
          className={cn(
            'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold shadow-lg active:scale-95 transition-transform',
            errorCount > 0
              ? 'bg-red-500 text-white'
              : 'bg-foreground/85 text-background',
          )}
        >
          <Bug className="w-3.5 h-3.5" />
          {errorCount > 0 && <span dir="ltr">{errorCount}</span>}
        </button>
      )}

      {/* Sheet — slides up from the bottom, covers most of the
          viewport so long stacks render readably. */}
      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40"
          onClick={() => setOpen(false)}
        >
          <Card
            className="w-full max-w-screen-sm rounded-b-none border-b-0 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <CardContent className="p-0 flex-1 min-h-0 flex flex-col">
              <div className="flex items-center gap-2 p-3 border-b border-border">
                <Bug className="w-4 h-4 text-app-primary-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground" dir="auto">
                    {t('debug.title')}
                  </p>
                  <p className="text-[11px] text-muted-foreground" dir="auto">
                    {t('debug.count', { count: entries.length, errors: errorCount })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label={t('debug.close')}
                  className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                {['all', 'warn', 'error'].map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFilter(f)}
                    aria-pressed={filter === f}
                    className={cn(
                      'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors',
                      filter === f
                        ? 'bg-app-primary-500 text-white'
                        : 'bg-muted text-muted-foreground hover:bg-accent',
                    )}
                  >
                    {t(`debug.filter.${f}`)}
                  </button>
                ))}
                <div className="flex-1" />
                <Button size="sm" variant="outline" onClick={handleCopy}>
                  {copied ? <Check className="w-3.5 h-3.5 me-1" /> : <Copy className="w-3.5 h-3.5 me-1" />}
                  {copied ? t('debug.copied') : t('debug.copy')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => clearEntries()}>
                  <Trash2 className="w-3.5 h-3.5 me-1" />
                  {t('debug.clear')}
                </Button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-1.5 font-mono text-[11px]" dir="ltr">
                {filtered.length === 0 ? (
                  <p className="text-muted-foreground py-4 text-center">{t('debug.empty')}</p>
                ) : filtered.map((e, i) => (
                  <div key={i} className={cn('break-words whitespace-pre-wrap', LEVEL_TONES[e.level] || '')}>
                    <span className="text-muted-foreground me-2">{new Date(e.ts).toLocaleTimeString('en-GB')}</span>
                    <span className="me-2 uppercase opacity-70">{e.level}</span>
                    <span>{e.text}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>,
    document.body,
  )
}
